define((require) => {
    "use strict";

    const ConfirmationManager           = require("services/ConfirmationManager");
    const {DateUtil}                      = require("shared/util/DateUtil");
    const EditorEvents                  = require("shared/services/EditorEvents");
    const ImprovedView                  = require("editor/zbone/ImprovedView");
    const ManageUsersHelper             = require("ui/settings/manageAccount/ManageUsersHelper");
    const ManageUsersItemViewController = require("ui/settings/manageAccount/ManageUsersItemViewController");
    const Permissions                   = require("shared/security/Permissions");
    const template                      = require("ui/settings/manageAccount/ManageUsersItemView.tpl");
    const Tooltip                       = require("ui/common/Tooltip");

    /**
     * The item view for a single user in the manage users view.
     *
     * @class ManageUsersItemView
     * @constructor
     *
     * @cfg {User}      model     The user model backing this view.
     * @cfg {Principal} principal The currently logged in principal (which may or may not be a principal for the configured user).
     * @cfg {String[]}  roles     The list of role names the principal can assign to other users in the same company.
     */
    return ImprovedView.extend({

        className: "user-profile",

        template: template,

        controller: ManageUsersItemViewController,

        templateHelpers() {
            return {
                isLoggedInUser: this._isLoggedInUser,
                accessedAt:     this._accessedAt
            };
        },

        events: {
            "click .delete": "_deleteUser"
        },

        ui: {
            userPermissionsDisplay: ".user-permissions-display",
            userPermissionsChangeOptions:  ".user-permissions-change-options",
            delete: ".delete"
        },

        bindings: {
            ".user-name": "displayName",
            ".accessed-at": {
                observe: "accessedAt",
                onGet(date) {
                    return "Last login: " + (date ? DateUtil.formatYYYYMMDDHHmm(date) : "Never");
                }
            },
            ".user-confirmed": {
                observe: "confirmedAt",
                onGet() {
                    if (ConfirmationManager.confirmationRequired(this.model)) {
                        return "(Unconfirmed)";
                    }
                }
            },
            ".user-email": "username",
            ".user-permissions-display": {
                observe: "role",
                onGet(role) {
                    return ManageUsersHelper.createRoleLabel(role.name);
                }
            },
            ".user-permissions-select": {
                observe: "role",
                selectOptions: {
                    collection() {
                        return ManageUsersHelper.mapRoles(this.options.roles);
                    },
                    labelPath: "roleLabel",
                    valuePath: "role"
                }
            }
        },

        modelEvents: {
            "change:role"() {
                // See ManageUsersItemViewController
                this.trigger("roleChanged");
            }
        },

        _isLoggedInUser: null,
        _role: null,
        _accessedAt: null,

        initialize() {
            const principal = this.options.principal;
            // Logged in principal for same user as the model user for this item view?
            this._isLoggedInUser = principal.forUser(this.model);
            // Already assigned role for the user backing this view...
            this._role = this.model.get("role");
            this._accessedAt = principal.get("type") === "AD";
        },

        onRender() {
            // Does the principal have access to the role of the user, i.e. can the principal change the role of
            // the given user? In addition, a user cannot changer her own permissions...
            if (this._isLoggedInUser || !this.options.roles.length || this.options.principal.cannot(this.model)) {
                this.ui.userPermissionsChangeOptions.hide();
            } else {
                this.ui.userPermissionsDisplay.hide();
            }

            this.ui.delete.toggleClass("hidden", !Permissions.Roles.MANAGER.can());

            if (this._isLoggedInUser) {
                this.$el.addClass("current-user");
            }
        },

        onShow() {
            if (this.ui.delete.is(":visible")) {
                Tooltip.add(this.ui.delete, {
                    placement: "top",
                    width: 200
                });
            }
        },

        _deleteUser() {
            const name = this.model.get("displayName");
            const role = this.model.get("role").name;
            const username = this.model.get("username");
            const promptMessage = `Are you sure you want to permanently delete ${name}? The user will no longer be able to login`;
            if (confirm(promptMessage)) {
                return this.model.destroy({wait: true}).then(() => {
                    const eventProperties = {
                        name: name,
                        username: username,
                        role: role,
                        userCreationType: "creator",
                        triggerOrigin: "DeleteUserConfirmOkButton"
                    };

                    EditorEvents.trigger(EditorEvents.constants.DELETE_USER, eventProperties);

                    return this.model;
                });
            }
        }
    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/settings/manageAccount/ManageUsersItemView.js